import { supabase } from '../../lib/supabase';

// `rpcUnknown` escape hatch (same pattern as ./api.ts): the Phase 159 RPCs
// aren't in the generated types until the user re-runs `supabase gen types`, so
// we narrow through `unknown` to stay typecheck-clean.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type OpenAttemptKind = 'scheduled' | 'practice' | 'assignment';

/**
 * One unfinished attempt, exactly as `student_open_attempts()` returns it.
 *
 * `remaining_ms` is computed server-side and already accounts for the pause
 * credit — it is the time the student will actually have after resuming, not a
 * number the browser derived from a start timestamp.
 */
export interface OpenAttemptRow {
  attempt_id: string;
  kind: OpenAttemptKind;
  test_id: string;
  test_title: string;
  test_status: string;
  exam_type: 'academic' | 'general';
  session_id: string | null;
  session_status: string | null;
  session_ends_at: string | null;
  assignment_id: string | null;
  assignment_due_at: string | null;
  started_at: string;
  /** Last moment the student was known to be working on it. */
  last_active_at: string;
  paused: boolean;
  pausable: boolean;
  total_ms: number;
  remaining_ms: number;
  section_count: number;
  /** 1-based position of the section they were on. */
  section_index: number;
  current_section_kind: string | null;
  question_count: number;
  answered_count: number;
}

export const OPEN_ATTEMPTS_QUERY_KEY = ['student', 'open_attempts'] as const;

export async function listOpenAttempts(): Promise<OpenAttemptRow[]> {
  const { data, error } = await rpcUnknown.rpc('student_open_attempts');
  if (error !== null) throw new Error(error.message);
  return (data as OpenAttemptRow[] | null) ?? [];
}

export interface DiscardResult {
  ok: boolean;
  error?: string;
}

/**
 * Throw an unfinished PRACTICE attempt away. Assignments and scheduled mocks
 * refuse with `not_discardable`: an assignment is one-shot and examiner-graded,
 * and a booked sitting is the institute's to cancel, not the student's.
 */
export async function discardAttempt(attemptId: string): Promise<DiscardResult> {
  const { data, error } = await rpcUnknown.rpc('student_discard_attempt', {
    p_attempt_id: attemptId,
  });
  if (error !== null) return { ok: false, error: error.message };
  return (data as DiscardResult | null) ?? { ok: false, error: 'no_body' };
}
